'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import { experiences, computeDuration } from '../data/experience'
import { pathStore } from '../store/pathStore'

gsap.registerPlugin(ScrollTrigger, useGSAP)

/* ── Small hex SVG badge ── */
function HexDot({ filled = false, size = 8 }: { filled?: boolean; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
      <polygon
        points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5"
        fill={filled ? 'var(--color-fire)' : 'none'}
        stroke="var(--color-fire)"
        strokeWidth={filled ? '0' : '10'}
      />
    </svg>
  )
}

/**
 * SCENE 4: THE PATH (Experience)
 * Horizontal scroll cinema — each experience is a fullscreen slide.
 * HexField particles disperse into an ambient orbit during this section,
 * pulsing with each job transition.
 */
export default function Path() {
  const [durationReference, setDurationReference] = useState<Date | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  // ── Career-trace conductor refs ──
  const litRef = useRef<HTMLDivElement>(null)          // energized fire portion (grows to head)
  const headRef = useRef<HTMLDivElement>(null)         // bright current head riding the trace
  const nodeRefs = useRef<(HTMLDivElement | null)[]>([]) // one hex solder-joint per slide
  const prevSlideRef = useRef<number>(-1)              // last ignited slide (drives ignition)

  // "Present" durations depend on the visitor's current month. Resolve that only
  // after hydration so statically generated HTML never disagrees with the client.
  useEffect(() => {
    const frame = requestAnimationFrame(() => setDurationReference(new Date()))
    return () => cancelAnimationFrame(frame)
  }, [])

  useGSAP(
    () => {
      if (!containerRef.current || !trackRef.current) return

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

      const slides = trackRef.current.querySelectorAll('.path-slide')
      const totalWidth = slides.length * window.innerWidth
      const scrollDistance = totalWidth - window.innerWidth
      const nSlides = slides.length

      // Horizontal position (% of full track width) of the current head at a
      // given scroll progress. Head sits under the currently-centred slide:
      //   p=0  → node 0 centre,   p=1 → last node centre.
      const headPctAt = (p: number) => ((0.5 + p * (nSlides - 1)) / nSlides) * 100

      // Ignite nodes 0..slide (hollow → filled + glow); dim nodes ahead.
      // Wired off the pathStore.slide change that drives the logo cross-fade.
      const igniteNodes = (slide: number, instant = false) => {
        const nodes = nodeRefs.current
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i]
          if (!node) continue
          const filled = node.querySelector('.node-filled')
          const year = node.querySelector('.node-year')
          const lit = i <= slide
          const yearColor = lit ? 'var(--color-fire)' : 'var(--color-stone)'
          if (instant || reduce) {
            gsap.set(filled, { opacity: lit ? 1 : 0 })
            gsap.set(year, { color: yearColor })
          } else {
            gsap.to(filled, { opacity: lit ? 1 : 0, duration: 0.45, ease: 'power2.out' })
            gsap.to(year, { color: yearColor, duration: 0.45, ease: 'power2.out' })
          }
        }
        // Quick ignition pulse on the node just reached (motion only, forward).
        if (!instant && !reduce && slide >= 0) {
          const glow = nodeRefs.current[slide]?.querySelector('.node-glow')
          if (glow) {
            gsap.fromTo(
              glow,
              { opacity: 0.85, scale: 0.5 },
              { opacity: 0, scale: 2.4, duration: 0.7, ease: 'power2.out' }
            )
          }
        }
      }

      // Horizontal scroll
      gsap.to(trackRef.current, {
        x: -scrollDistance,
        ease: 'none',
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top top',
          end: `+=${scrollDistance}`,
          pin: true,
          scrub: 1,
          anticipatePin: 1,
          onLeaveBack: () => {
            pathStore.slide = -1
            pathStore.phase = 0
          },
          onUpdate: (self) => {
            // ── Drive the horizontal career-trace current ──
            const headPct = headPctAt(self.progress)
            if (litRef.current) litRef.current.style.width = `${headPct}%`
            if (headRef.current) headRef.current.style.left = `${headPct}%`

            // Update pathStore so HexField knows which company logo to form
            const n = experiences.length
            const slideF = self.progress * n
            const slide = Math.min(Math.floor(slideF), n - 1)
            const localP = slideF - Math.floor(slideF)
            const entryPhase = Math.min(localP * 3, 1)
            pathStore.slide = slide
            pathStore.phase = entryPhase

            // Ignite / dim solder-joints when the current slide changes.
            if (slide !== prevSlideRef.current) {
              prevSlideRef.current = slide
              igniteNodes(slide)
            }
          },
        },
      })

      // Animate each slide content on enter
      slides.forEach((slide, i) => {
        const els = {
          index:        slide.querySelector('.path-index'),
          logo:         slide.querySelector('.path-logo'),
          company:      slide.querySelector('.path-company'),
          role:         slide.querySelector('.path-role'),
          meta:         slide.querySelector('.path-meta'),
          achievements: slide.querySelectorAll('.path-achievement'),
          side:         slide.querySelector('.path-side'),
        }

        const slideStart = i / slides.length
        const slideEnd   = (i + 1) / slides.length

        ScrollTrigger.create({
          trigger: containerRef.current,
          start: 'top top',
          end: `+=${scrollDistance}`,
          scrub: 0.5,
          onUpdate: (self) => {
            const p = self.progress
            const slideWidth = slideEnd - slideStart   // = 1 / slides.length

            // ── Lead-in: start appearing 30% of a slide-width BEFORE slideStart ──
            // so the incoming slide is already partially visible while the
            // previous slide is still fading out (no dead black gap).
            const LEAD       = 0.30
            const earlyStart = Math.max(0, slideStart - LEAD * slideWidth)
            const inRange    = p >= earlyStart && p <= slideEnd
            if (!inRange) return

            // localP: 0 at slideStart, negative during lead-in, 1 at slideEnd
            const localP = (p - slideStart) / slideWidth

            // Entry: 0 at earlyStart (localP = -LEAD), 1 at localP ≈ 0.06
            // (fully visible very quickly once the slide is centred)
            const ENTRY_RANGE = LEAD + 0.06
            const entry = i === 0 ? 1 : Math.min((localP + LEAD) / ENTRY_RANGE, 1)

            // ── Exit: starts at 82%, done at 98% ── (last slide never exits)
            const EXIT_START = 0.82
            const EXIT_END   = 0.98
            const exitT  = Math.max(0, Math.min(1, (localP - EXIT_START) / (EXIT_END - EXIT_START)))
            const isLast = i === slides.length - 1
            const exitFade = isLast ? 1 : 1 - exitT

            const vis  = entry * exitFade
            const vis2 = Math.max(0, vis - 0.12) * 1.14
            const vis3 = Math.max(0, vis - 0.22) * 1.29

            // Company: wipe in from left (right edge appears first, matching right→left travel)
            const clipPct = i === 0 ? 0 : Math.round((1 - entry) * 100)

            gsap.set(els.index,   { opacity: vis,  y: (1 - entry) * -22 })
            gsap.set(els.logo,    { opacity: vis,  y: (1 - entry) *  28 })
            gsap.set(els.company, {
              opacity: vis,
              clipPath: `inset(0 0 0 ${clipPct}%)`,
            })
            gsap.set(els.role,    { opacity: vis2, y: (1 - Math.min(entry * 1.3, 1)) * 18 })
            gsap.set(els.meta,    { opacity: vis3 })
            gsap.set(els.side,    { opacity: vis3, x: (1 - Math.min(entry * 1.5, 1)) * 30 })
            els.achievements.forEach((a, j) => {
              const d = Math.max(0, entry - 0.18 - j * 0.05) * 1.4
              gsap.set(a, { opacity: Math.min(d, 1) * exitFade, y: (1 - Math.min(d, 1)) * 14 })
            })
          },
        })
      })

      // Slide 0 is already centred when the section pins.
      // GSAP's onUpdate never fires at progress=0, so force all its
      // elements to their fully-visible state immediately.
      const s0 = slides[0]
      if (s0) {
        gsap.set(s0.querySelector('.path-index'),   { opacity: 1, y: 0 })
        gsap.set(s0.querySelector('.path-logo'),    { opacity: 1, y: 0 })
        gsap.set(s0.querySelector('.path-company'), { opacity: 1, clipPath: 'inset(0 0 0 0%)' })
        gsap.set(s0.querySelector('.path-role'),    { opacity: 1, y: 0 })
        gsap.set(s0.querySelector('.path-meta'),    { opacity: 1 })
        gsap.set(s0.querySelector('.path-side'),    { opacity: 1, x: 0 })
        s0.querySelectorAll('.path-achievement').forEach((a) =>
          gsap.set(a, { opacity: 1, y: 0 })
        )
        // Tell HexField to form logo 0 immediately — no scroll needed.
        pathStore.slide = 0
        pathStore.phase = 1
      }

      // Seat the career-trace at slide 0: head + energized portion at node 0,
      // first solder-joint ignited (instant, no pulse — this is the resting state).
      const startPct = headPctAt(0)
      if (litRef.current) litRef.current.style.width = `${startPct}%`
      if (headRef.current) headRef.current.style.left = `${startPct}%`
      prevSlideRef.current = 0
      igniteNodes(0, true)
    },
    { scope: containerRef }
  )

  return (
    <section
      ref={containerRef}
      id="path"
      data-scene="path"
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: 'var(--color-void)' }}
    >
      {/* ── Base dot grid (gray) ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle 1px at center, rgba(140,140,140,0.12) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          zIndex: 0,
        }}
        aria-hidden="true"
      />

      {/* ── Orange dot overlay (radial center fade) ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle 1px at center, rgba(255,107,0,0.18) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          maskImage: 'radial-gradient(ellipse 50% 60% at 50% 50%, black, transparent)',
          WebkitMaskImage: 'radial-gradient(ellipse 50% 60% at 50% 50%, black, transparent)',
          zIndex: 0,
        }}
        aria-hidden="true"
      />

      {/* Scene label */}
      <div
        className="absolute flex items-center gap-3"
        style={{ zIndex: 5, top: 'clamp(5rem, 8vh, 6.5rem)', left: 'clamp(1.5rem, 4vw, 3.5rem)' }}
      >
        <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-fog)' }}>04</span>
        <HexDot />
        <h2 className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-fog)' }}>The Path</h2>
        <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-stone)' }}>/ experience</span>
      </div>

      {/* ── Horizontal track ── */}
      <div
        ref={trackRef}
        className="flex"
        style={{ width: `${experiences.length * 100}vw`, position: 'relative', zIndex: 3 }}
      >
        {experiences.map((exp, i) => {
          const startYear = exp.dateRange.split('—')[0].trim().split(' ').pop()
          return (
          <div
            key={exp.id}
            className="path-slide w-screen shrink-0 flex items-center relative"
            style={{ height: '100dvh', paddingLeft: 'clamp(2rem, 8vw, 10rem)', paddingRight: 'clamp(2rem, 6vw, 6rem)' }}
          >
            {/* ── Decorative large index number ── */}
            <div
              className="path-index absolute select-none pointer-events-none"
              style={{
                right: 'clamp(2rem, 6vw, 8rem)',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 'clamp(14rem, 30vw, 40rem)',
                lineHeight: 0.85,
                fontWeight: 900,
                letterSpacing: '-0.04em',
                color: 'transparent',
                WebkitTextStroke: '1px rgba(255,107,0,0.05)',
                fontFamily: 'var(--font-bevellier, var(--font-display))',
                userSelect: 'none',
                opacity: 0,
              }}
              aria-hidden="true"
            >
              {startYear}
            </div>

            {/* ── Main content ── */}
            <div className="relative flex flex-col md:flex-row md:items-start gap-8 md:gap-16 max-w-6xl w-full">

              {/* Left column: primary info */}
              <div className="flex-1 min-w-0">

                {/* Logo pill */}
                <div className="path-logo flex items-center gap-4 mb-5 opacity-0">
                  <div
                    style={{
                      width: 'clamp(44px, 4.5vw, 64px)',
                      height: 'clamp(44px, 4.5vw, 64px)',
                      borderRadius: '12px',
                      background: 'rgba(255,255,255,0.04)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255,107,0,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '8px',
                      flexShrink: 0,
                    }}
                  >
                    <Image
                      src={exp.logo}
                      alt={exp.company}
                      width={64}
                      height={64}
                      sizes="64px"
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'contain', filter: 'brightness(1.1)' }}
                    />
                  </div>

                  {/* Type + location badges */}
                  <div className="flex flex-wrap gap-2">
                    <span
                      className="font-mono uppercase"
                      style={{
                        fontSize: 'clamp(0.5rem, 0.7vw, 0.6rem)',
                        letterSpacing: '0.18em',
                        color: 'var(--color-fire)',
                        background: 'rgba(255,107,0,0.08)',
                        border: '1px solid rgba(255,107,0,0.2)',
                        padding: '3px 10px',
                        borderRadius: '3px',
                      }}
                    >
                      {exp.type}
                    </span>
                    <span
                      className="font-mono uppercase"
                      style={{
                        fontSize: 'clamp(0.5rem, 0.7vw, 0.6rem)',
                        letterSpacing: '0.18em',
                        color: 'var(--color-fog)',
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.06)',
                        padding: '3px 10px',
                        borderRadius: '3px',
                      }}
                    >
                      {exp.locationType}
                    </span>
                  </div>
                </div>

                {/* Company name — large display type (h3 under the section h2) */}
                <h3
                  className="path-company font-bevellier font-extrabold leading-none opacity-0"
                  style={{
                    fontSize: 'clamp(2rem, 5.5vw, 5rem)',
                    color: 'var(--color-light)',
                    letterSpacing: '-0.03em',
                  }}
                >
                  {exp.company}
                </h3>

                {/* Spacing where the per-slide line used to sit — the trace is
                    now the single continuous conductor along the baseline. */}
                <div className="mt-3 mb-5" style={{ height: '2px' }} />

                {/* Role */}
                <p
                  className="path-role font-body opacity-0"
                  style={{
                    fontSize: 'clamp(0.95rem, 1.6vw, 1.3rem)',
                    color: 'var(--color-mist)',
                    marginBottom: '0.6rem',
                    fontWeight: 400,
                  }}
                >
                  {exp.role}
                </p>

                {/* Meta: date · duration · location */}
                <div className="path-meta flex flex-wrap items-center gap-x-3 gap-y-1 mb-7 opacity-0">
                  <span className="font-mono" style={{ fontSize: 'clamp(0.55rem, 0.85vw, 0.75rem)', color: 'var(--color-fog)' }}>
                    {exp.dateRange}
                  </span>
                  <HexDot filled size={5} />
                  <span className="font-mono" style={{ fontSize: 'clamp(0.55rem, 0.85vw, 0.75rem)', color: 'var(--color-fog)' }}>
                    {exp.dateRange.endsWith('Present')
                      ? durationReference ? computeDuration(exp.dateRange, durationReference) : 'Ongoing'
                      : computeDuration(exp.dateRange)}
                  </span>
                  <HexDot filled size={5} />
                  <span className="font-mono" style={{ fontSize: 'clamp(0.55rem, 0.85vw, 0.75rem)', color: 'var(--color-stone)' }}>
                    {exp.location}
                  </span>
                </div>
              </div>

              {/* Right column: achievements */}
              <div
                className="path-side flex-1 min-w-0 max-w-md opacity-0"
                style={{ paddingTop: 'clamp(0rem, 2vw, 2rem)' }}
              >
                <span
                  className="font-mono uppercase block mb-4"
                  style={{ fontSize: 'clamp(0.5rem, 0.7vw, 0.6rem)', letterSpacing: '0.25em', color: 'var(--color-stone)' }}
                >
                  Key Contributions
                </span>

                <div className="space-y-3">
                  {exp.achievements.map((achievement, j) => (
                    <div key={j} className="path-achievement flex items-start gap-3 opacity-0" style={{ marginTop: '6px' }}>
                      <div>
                        <HexDot filled size={7} />
                      </div>
                      <p
                        className="font-body"
                        style={{
                          fontSize: 'clamp(0.78rem, 1vw, 0.95rem)',
                          color: 'var(--color-fog)',
                          lineHeight: 1.6,
                        }}
                      >
                        {achievement}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Slide counter — bottom right */}
            <div
              className="absolute bottom-6 right-6 lg:bottom-10 lg:right-10 font-mono flex items-center gap-2"
              style={{ fontSize: 'clamp(0.55rem, 0.75vw, 0.65rem)', letterSpacing: '0.18em', color: 'var(--color-stone)' }}
            >
              <span style={{ color: 'var(--color-fire)' }}>{String(i + 1).padStart(2, '0')}</span>
              <span style={{ color: 'rgba(255,107,0,0.25)' }}>/</span>
              <span>{String(experiences.length).padStart(2, '0')}</span>
            </div>
          </div>
          )
        })}

        {/* ── Career-trace conductor: one continuous horizontal current ──
            A dim copper rail spanning the whole track at a fixed baseline,
            with a hex solder-joint seated at each slide centre. Everything
            behind the fire head is energized; everything ahead is unlit. */}
        <div
          className="absolute pointer-events-none"
          style={{ left: 0, right: 0, bottom: 'clamp(3rem, 7vh, 5rem)', height: '2px', zIndex: 1 }}
          aria-hidden="true"
        >
          {/* dim copper base line (ahead of the head) */}
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(184,115,51,0.20)' }} />

          {/* energized fire portion — grows to the head in onUpdate */}
          <div
            ref={litRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '0%',
              background: 'linear-gradient(90deg, rgba(255,69,0,0.45) 0%, var(--color-fire) 100%)',
              boxShadow: '0 0 10px rgba(255,107,0,0.4)',
              willChange: 'width',
            }}
          />

          {/* hex solder-joints — one per slide centre */}
          {experiences.map((exp, i) => {
            const year = exp.dateRange.split('—')[0].trim().split(' ').pop()
            const leftPct = ((i + 0.5) / experiences.length) * 100
            return (
              <div
                key={exp.id}
                ref={(el) => { nodeRefs.current[i] = el }}
                className="path-node"
                style={{ position: 'absolute', top: '50%', left: `${leftPct}%`, width: 0, height: 0 }}
              >
                {/* ignition pulse ring */}
                <div
                  className="node-glow"
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    marginLeft: '-18px',
                    marginTop: '-18px',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(255,107,0,0.55), transparent 70%)',
                    opacity: 0,
                  }}
                />
                {/* hollow (unlit) hex */}
                <div
                  className="node-hollow"
                  style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', lineHeight: 0 }}
                >
                  <HexDot size={14} />
                </div>
                {/* filled (ignited) hex — cross-faded in on ignition */}
                <div
                  className="node-filled"
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    lineHeight: 0,
                    opacity: 0,
                    filter: 'drop-shadow(0 0 5px rgba(255,107,0,0.85))',
                  }}
                >
                  <HexDot filled size={14} />
                </div>
                {/* year tick */}
                <span
                  className="node-year font-mono"
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '14px',
                    transform: 'translateX(-50%)',
                    fontSize: '0.6rem',
                    letterSpacing: '0.15em',
                    color: 'var(--color-stone)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {year}
                </span>
              </div>
            )
          })}

          {/* bright current head riding the trace */}
          <div
            ref={headRef}
            style={{
              position: 'absolute',
              top: '50%',
              left: '0%',
              width: '12px',
              height: '12px',
              transform: 'translate(-50%, -50%)',
              willChange: 'left',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                background: 'var(--color-fire)',
                boxShadow: '0 0 14px 3px rgba(255,107,0,0.7)',
              }}
            />
          </div>
        </div>
      </div>
    </section>
  )
}
